import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    Box,
    FormControl,
    IconButton,
    InputAdornment,
    OutlinedInput,
    makeStyles,
} from '@material-ui/core'
import StopIcon from '@material-ui/icons/Stop'
import SendIcon from 'components/RightPane/SendIcon'
import useBetaWorkspaceToggle from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceRightPane/hooks/useBetaWorkspaceToggle'
import React from 'react'
import { useChatBot } from './useChatBotHook'

export interface MessagePromptStylesProps {
    isBetaWorkspaceToggleEnabled?: boolean
}

const useStyles = makeStyles((theme) => ({
    promptContainer: {
        width: '100%',
        alignSelf: 'center',
        padding: theme.spacing(1.25),
    },
    promptForm: {
        width: '100%',
    },
    promptInput: (props: MessagePromptStylesProps) => ({
        height: props.isBetaWorkspaceToggleEnabled ? pxToRem(38) : 'auto',
        backgroundColor: props.isBetaWorkspaceToggleEnabled
            ? '#FFFFFF'
            : 'transparent',
        borderRadius: props.isBetaWorkspaceToggleEnabled ? pxToRem(8) : '100px',
        border: props.isBetaWorkspaceToggleEnabled
            ? 'none !important'
            : 'initial',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        fontSize: props.isBetaWorkspaceToggleEnabled
            ? pxToRem(12)
            : pxToRem(14),
        fontFamily: 'Roboto',
    }),

    promptIcon: (props: MessagePromptStylesProps) => ({
        fontSize: pxToRem(20),
        ...(props.isBetaWorkspaceToggleEnabled && {
            color: '#31353D',
            width: pxToRem(18),
            height: pxToRem(18),
        }),
    }),
    sendIconContainer: {
        marginLeft: pxToRem(10),
    },
    customNotchedOutline: {
        borderStyle: 'none !important',
    },
}))

export interface MessagePromptProps {
    ref?: React.RefObject<HTMLInputElement>
}

const MessagePrompt = ({ ref }: MessagePromptProps) => {
    const isBetaWorkspaceToggleEnabled = useBetaWorkspaceToggle()
    const classes = useStyles({ isBetaWorkspaceToggleEnabled })

    const {
        isLoading,
        createUserMessage,
        askMessage,
        cancelAskMessage,
    } = useChatBot()

    const [userInput, setUserInput] = React.useState<string>('')

    const handleSetInput = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setUserInput(event.target.value)
        },
        []
    )

    const handleAskMessage = React.useCallback(() => {
        const prompt = createUserMessage(userInput)
        askMessage(prompt)

        // Clear the input field
        setUserInput('')
    }, [userInput, askMessage])

    const handleCancellation = React.useCallback(() => {
        cancelAskMessage()
    }, [cancelAskMessage])

    const handleSubmit = React.useCallback(
        (event: React.FormEvent<HTMLElement>) => {
            event.preventDefault()

            // Only handle asking a message on submit,
            // because it's annoying when enter was pressed and it cancels the message
            if (!isLoading) {
                handleAskMessage()
            }
        },
        [isLoading, handleCancellation, handleAskMessage]
    )

    const handleClick = React.useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            event.preventDefault()
            if (isLoading) {
                // we're currently asking a message and stop button was clicked, so cancel
                handleCancellation()
            } else {
                handleAskMessage()
            }
        },
        [isLoading, handleCancellation, handleAskMessage]
    )

    const submitText = isLoading ? 'Stop' : 'Send'

    return (
        <Box
            component="form"
            className={classes.promptContainer}
            onSubmit={handleSubmit}
        >
            <FormControl className={classes.promptForm}>
                <OutlinedInput
                    id="askDelaInput"
                    type="text"
                    margin="dense"
                    placeholder="Ask Dela..."
                    value={userInput}
                    className={classes.promptInput}
                    data-testid="askDelaInput"
                    role="textbox"
                    aria-label="Ask Dela"
                    autoComplete="off"
                    ref={ref}
                    onChange={handleSetInput}
                    classes={{
                        notchedOutline:
                            isBetaWorkspaceToggleEnabled &&
                            classes.customNotchedOutline,
                    }}
                    endAdornment={
                        <InputAdornment position="end">
                            <HoverTooltip
                                text={submitText}
                                placements={['top']}
                            >
                                <IconButton
                                    id="askDelaButton"
                                    type="button"
                                    aria-label={submitText}
                                    role="button"
                                    color="primary"
                                    size="small"
                                    data-testid="askDelaButton"
                                    onClick={handleClick}
                                >
                                    {isLoading ? (
                                        <StopIcon
                                            className={classes.promptIcon}
                                            data-testid="stopPrompt"
                                        />
                                    ) : (
                                        <div
                                            className={
                                                isBetaWorkspaceToggleEnabled &&
                                                classes.sendIconContainer
                                            }
                                        >
                                            <SendIcon
                                                className={classes.promptIcon}
                                                data-testid="sendPrompt"
                                            />
                                        </div>
                                    )}
                                </IconButton>
                            </HoverTooltip>
                        </InputAdornment>
                    }
                />
            </FormControl>
        </Box>
    )
}

export default MessagePrompt
